export interface Vector2D {
    x: number
    y: number
}

export interface VectorLine {
    a: Vector2D
    b: Vector2D
}

export interface SightPoint extends Vector2D {
    /** Distance from the eye along this vertex's own ray, in world pixels. */
    param: number
    /** How square-on the ray met the surface it stopped against (1 head-on, 0 grazing). */
    normalCos: number
    angle: number
}

// How far INTO a surface a sightline still reads as lit. Cave occluders sit on the rock's
// true tile boundary (see buildPolyMap), so without this the rock itself would be solid
// black and you could not see what was blocking you; revealing half a tile shows the face
// of the wall or deposit you stopped against. Because every vertex moves out along its own
// ray the polygon stays star-shaped about the eye, so it cannot fold over itself.
export const WallFaceReveal = 16

// A ray meeting a face obliquely only gets WallFaceReveal * cos(angle) into the rock, so a
// wall seen along its length would show as a sliver rather than half a tile. Dividing the
// push by that cosine restores the depth, and this caps the correction: a ray grazing a face
// almost parallel would otherwise be pushed an unbounded distance past the corner it grazes.
const MaxRevealStretch = 2.5

// The furthest a sightline can ever reach past a surface, once that correction is capped.
export const MaxWallFaceReveal = WallFaceReveal * MaxRevealStretch

// Rays aimed exactly at a shared endpoint can land a few trillionths past it after rounding.
// Rejecting both meeting segments opens a bright wedge, so allow this much slop along a segment.
const EndpointSlop = 1e-8

// Below this the ray and the segment are parallel: no single intersection point.
const ParallelEpsilon = 1e-10

// How far either side of a vertex the flanking rays are aimed. Each one has to slip PAST the
// corner and carry on to whatever stands behind it, so the miss distance it buys at the corner
// (distance * this) must stay clear of EndpointSlop scaled to the segment's length.
const CornerNudge = 0.00001

// Scratch buffers. getSightPolygon is called several times per frame (once for the viewer, once
// per light) and used to allocate one object per ray/segment hit test -- ~300k allocations a frame
// in a cave. These are reused across calls instead; the function never recurses, so a single set
// is enough. They grow to the largest scene seen and are never shrunk.
let segAx = new Float64Array(0)
let segAy = new Float64Array(0)
let segBx = new Float64Array(0)
let segBy = new Float64Array(0)
let segInvLen = new Float64Array(0)
let rayAngles = new Float64Array(0)

const ensureSegmentCapacity = (n: number) => {
    if (segAx.length >= n) return
    const size = Math.max(n, segAx.length * 2, 64)
    segAx = new Float64Array(size)
    segAy = new Float64Array(size)
    segBx = new Float64Array(size)
    segBy = new Float64Array(size)
    segInvLen = new Float64Array(size)
}

const ensureAngleCapacity = (n: number) => {
    if (rayAngles.length >= n) return
    rayAngles = new Float64Array(Math.max(n, rayAngles.length * 2, 192))
}

// castRay's results. Returning an object per ray is what this rewrite exists to avoid, and
// neither caster recurses, so a pair of scalars read straight after the call is enough.
let hitParam = 0
let hitNormalCos = 0

/**
 * Nearest of the flattened segments that the unit ray (rdx, rdy) from (sightX, sightY) crosses,
 * left in hitParam (a distance in world pixels, since the ray is a unit vector) and hitNormalCos
 * (how square-on it met that segment: 1 head-on, 0 grazing).
 */
const castRay = (sightX: number, sightY: number, rdx: number, rdy: number, segmentCount: number) => {
    let bestParam = Infinity
    let bestNormalCos = 0
    for (let i = 0; i < segmentCount; i++) {
        const sdx = segBx[i] - segAx[i]
        const sdy = segBy[i] - segAy[i]
        // Already |segment| * sin(between them), which is why normalCos below only has to divide
        // the segment's length back out.
        const denominator = rdx * sdy - rdy * sdx
        if (denominator > -ParallelEpsilon && denominator < ParallelEpsilon) continue
        const offsetX = segAx[i] - sightX
        const offsetY = segAy[i] - sightY
        const T1 = (offsetX * sdy - offsetY * sdx) / denominator
        // Reject before computing T2: a hit further away than the best so far cannot win. The
        // negated form also rejects a NaN T1, which is what the old explicit finite checks caught.
        if (!(T1 >= 0) || T1 >= bestParam) continue
        const T2 = (offsetX * rdy - offsetY * rdx) / denominator
        if (T2 < -EndpointSlop || T2 > 1 + EndpointSlop) continue
        bestParam = T1
        bestNormalCos = Math.abs(denominator) * segInvLen[i]
    }
    if (bestParam === Infinity) return false
    hitParam = bestParam
    hitNormalCos = bestNormalCos
    return true
}

// A ray meeting a face obliquely only gets `reveal` * cos(angle) into it, so dividing by that
// cosine restores the depth -- capped, or a ray grazing a face almost parallel to it would be
// pushed an unbounded distance past the corner it grazed.
const revealAlong = (reveal: number, normalCos: number) =>
    reveal / Math.max(normalCos, 1 / MaxRevealStretch)

/**
 * The visibility polygon around (sightX, sightY), as vertices ordered by descending angle.
 *
 * Three rays go out per distinct segment endpoint -- one just short of the corner, one straight
 * at it, one just past -- and each stops at the nearest segment it crosses. `reveal` then pushes
 * every vertex that far further along its own ray, so the face it stopped against stays lit (see
 * WallFaceReveal).
 */
export const getSightPolygon = (
    segments: Array<VectorLine>,
    sightX: number,
    sightY: number,
    reveal = 0,
): SightPoint[] => {
    const segmentCount = segments.length
    ensureSegmentCapacity(segmentCount)
    ensureAngleCapacity(segmentCount * 6)

    // Flatten the segments and collect three ray angles per endpoint.
    let angleCount = 0
    for (let i = 0; i < segmentCount; i++) {
        const { a, b } = segments[i]
        segAx[i] = a.x
        segAy[i] = a.y
        segBx[i] = b.x
        segBy[i] = b.y
        const dx = b.x - a.x
        const dy = b.y - a.y
        const length = Math.sqrt(dx * dx + dy * dy)
        segInvLen[i] = length > 0 ? 1 / length : 0
        for (let e = 0; e < 2; e++) {
            const angle = Math.atan2(
                (e === 0 ? a.y : b.y) - sightY,
                (e === 0 ? a.x : b.x) - sightX,
            )
            rayAngles[angleCount++] = angle - CornerNudge
            rayAngles[angleCount++] = angle
            rayAngles[angleCount++] = angle + CornerNudge
        }
    }

    // Sorting the ANGLES once is the same ordering the vertices used to be sorted into after the
    // fact, minus a comparator call per output vertex. Descending, to keep the winding the
    // callers' Graphics fills and Polygon.contains checks have always seen.
    //
    // It also subsumes deduplication, which used to be a Set of "x-y" keys over the endpoints --
    // two strings and a hash per segment, and the single biggest cost left in a small scene once
    // the hit loop got cheap. Equal angles are adjacent after the sort, so the skip below collapses
    // them for nothing. Deduping by ANGLE rather than by point is also strictly better: two
    // DIFFERENT endpoints that happen to line up behind each other from here share an angle, and
    // those used to get a second set of rays that could only add a zero-width spike to the result.
    const angles = rayAngles.subarray(0, angleCount)
    angles.sort()
    const points: SightPoint[] = []
    for (let k = angleCount - 1; k >= 0; k--) {
        const angle = angles[k]
        if (k < angleCount - 1 && angle === angles[k + 1]) continue
        const rdx = Math.cos(angle)
        const rdy = Math.sin(angle)
        if (!castRay(sightX, sightY, rdx, rdy, segmentCount)) continue

        const param = reveal ? hitParam + revealAlong(reveal, hitNormalCos) : hitParam
        points.push({
            x: sightX + rdx * param,
            y: sightY + rdy * param,
            param,
            normalCos: hitNormalCos,
            angle,
        })
    }

    return points
}

/**
 * The lit polygon around a light at (lightX, lightY) reaching `reach`, shaped by `blockers`.
 *
 * This used to go through getSightPolygon with the light's own circumference handed in as a ring
 * of occluding segments, which is where most of a light's cost went: those 64 segments were more
 * than every real occluder near the light put together, each was tested by every ray, and each
 * contributed two endpoints' worth of corner rays of its own -- for a boundary that cannot
 * occlude anything, being a convex ring the light sits at the centre of.
 *
 * Casting against the blockers alone and taking `reach` whenever nothing nearer stops the ray is
 * the same shape for a fraction of the work. It is also rounder: the ring's chords sat up to a
 * pixel inside the circle they stood in for, and this reaches `reach` exactly in every direction.
 */
export const getLightPolygon = (
    blockers: Array<VectorLine>,
    lightX: number,
    lightY: number,
    reach: number,
    reveal = 0,
    arcSamples = 32,
): SightPoint[] => {
    const segmentCount = blockers.length
    ensureSegmentCapacity(segmentCount)
    ensureAngleCapacity(segmentCount * 6 + arcSamples)

    let angleCount = 0
    for (let i = 0; i < segmentCount; i++) {
        const { a, b } = blockers[i]
        segAx[i] = a.x
        segAy[i] = a.y
        segBx[i] = b.x
        segBy[i] = b.y
        const dx = b.x - a.x
        const dy = b.y - a.y
        const length = Math.sqrt(dx * dx + dy * dy)
        segInvLen[i] = length > 0 ? 1 / length : 0
        for (let e = 0; e < 2; e++) {
            const angle = Math.atan2((e === 0 ? a.y : b.y) - lightY, (e === 0 ? a.x : b.x) - lightX)
            rayAngles[angleCount++] = angle - CornerNudge
            rayAngles[angleCount++] = angle
            rayAngles[angleCount++] = angle + CornerNudge
        }
    }
    // One ray per arc sample, not three: there is no corner here to slip past, only a smooth rim
    // to trace wherever no blocker interrupts it.
    for (let i = 0; i < arcSamples; i++) {
        rayAngles[angleCount++] = -Math.PI + (i * 2 * Math.PI) / arcSamples
    }

    const angles = rayAngles.subarray(0, angleCount)
    angles.sort()
    const points: SightPoint[] = []
    for (let k = angleCount - 1; k >= 0; k--) {
        const angle = angles[k]
        if (k < angleCount - 1 && angle === angles[k + 1]) continue
        const rdx = Math.cos(angle)
        const rdy = Math.sin(angle)

        // A blocker at or beyond `reach` cannot shade anything the light touches, so the rim wins.
        // The rim is met square-on from the centre, hence the undiminished reveal there.
        const stopped = castRay(lightX, lightY, rdx, rdy, segmentCount) && hitParam < reach
        const normalCos = stopped ? hitNormalCos : 1
        const param = (stopped ? hitParam : reach) + (reveal ? revealAlong(reveal, normalCos) : 0)
        points.push({
            x: lightX + rdx * param,
            y: lightY + rdy * param,
            param,
            normalCos,
            angle,
        })
    }

    return points
}

/**
 * Drop segments that cannot occlude anything within `size` of the eye, keeping the first
 * `skip + 1` entries whatever they are (callers reserve those slots for the view boundary).
 *
 * The test is the segment's bounding box against the eye's box. Comparing each ENDPOINT to the
 * box instead -- which this used to do -- discards a long wall whose ends both lie outside the
 * view while its middle crosses it, and light pours through the gap.
 */
export const pruneSegments = (
    segments: Array<VectorLine>,
    sightX: number,
    sightY: number,
    size = 100,
    skip = 0,
): Array<VectorLine> => {
    const left = sightX - size
    const right = sightX + size
    const top = sightY - size
    const bottom = sightY + size
    return segments.filter(
        ({ a, b }, i) =>
            i <= skip ||
            (Math.min(a.x, b.x) <= right &&
                Math.max(a.x, b.x) >= left &&
                Math.min(a.y, b.y) <= bottom &&
                Math.max(a.y, b.y) >= top),
    )
}

/**
 * Join axis-aligned segments that lie on the same line and touch or overlap, leaving the first
 * `reserved` entries untouched and in place.
 *
 * Occluders come off a tile grid, so one straight wall arrives as one segment per tile it spans
 * -- and buildPolyMap's exposed faces span a whole tile each, so neighbours overlap as well.
 * Every one of those is a separate ray target and a separate pair of hit tests for every other
 * ray. Merging them changes nothing about what is visible: the join between two collinear
 * segments is never a silhouette corner, and if a perpendicular face also ends there, that point
 * survives as ITS endpoint. Only touching or overlapping spans merge, never ones with a gap.
 */
export const mergeCollinearSegments = (segments: Array<VectorLine>, reserved = 0): Array<VectorLine> => {
    const horizontal = new Map<number, Array<[number, number]>>()
    const vertical = new Map<number, Array<[number, number]>>()
    const merged: Array<VectorLine> = []

    const span = (map: Map<number, Array<[number, number]>>, at: number, from: number, to: number) => {
        const spans = map.get(at)
        const entry: [number, number] = from <= to ? [from, to] : [to, from]
        if (spans) spans.push(entry)
        else map.set(at, [entry])
    }

    segments.forEach(({ a, b }, i) => {
        if (i < reserved) {
            merged.push({ a, b })
            return
        }
        if (a.y === b.y && a.x !== b.x) span(horizontal, a.y, a.x, b.x)
        else if (a.x === b.x && a.y !== b.y) span(vertical, a.x, a.y, b.y)
        // Diagonal bridges and degenerate segments have nothing collinear to join.
        else merged.push({ a, b })
    })

    const flush = (map: Map<number, Array<[number, number]>>, isHorizontal: boolean) => {
        map.forEach((spans, at) => {
            spans.sort((one, other) => one[0] - other[0])
            let [from, to] = spans[0]
            const emit = () =>
                merged.push(
                    isHorizontal
                        ? { a: { x: from, y: at }, b: { x: to, y: at } }
                        : { a: { x: at, y: from }, b: { x: at, y: to } },
                )
            for (let i = 1; i < spans.length; i++) {
                const [nextFrom, nextTo] = spans[i]
                if (nextFrom <= to) {
                    if (nextTo > to) to = nextTo
                    continue
                }
                emit()
                from = nextFrom
                to = nextTo
            }
            emit()
        })
    }

    flush(horizontal, true)
    flush(vertical, false)
    return merged
}
